import { useCallback, useState } from 'react';
import { Button } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { TableCell } from '../../components/table/Table';
import LocalDate from '../../components/LocalDate';
import { Tab } from './Tab';
import ActionButton from '../../components/ActionButton';
import {
  actions,
  selectors,
} from '../../store/dux/field-boundary-management-dux';
import { selectors as growerSelectors } from '../../store/dux/grower-dux';
import { EditAttributes } from './EditAttributes';
import { UncertifyFields } from './UncertifyFields';
import { ReassignFields } from './ReassignFields';
import { exportCertifiedFields as exportCsv } from '../../services/field-boundary-management-service';
import { usePermissions } from '../../hooks/use-permissions';
import { useFeatureFlag } from '../../hooks/use-feature-flag';
import * as soilService from '../../services/soil-service';
import * as elevationService from '../../services/elevation-service';
import * as hexbinService from '../../services/hexbin-service';
import * as vpiService from '../../services/product-placement-vpi-service';
import { useAlerts } from '../../hooks/use-alerts';
import { DownloadButton } from '../../components/DownloadButton';
import { ElevationDialog } from './ElevationDialog';
import { usePermission } from '../../hooks/use-permission';

export function CertifiedTab({ isActive, renderTabs }) {
  const dispatch = useDispatch();
  const [editAttributes, setEditAttributes] = useState();
  const [uncertifyFields, setUncertifyFields] = useState();
  const [reassignFields, setReassignFields] = useState();
  const [showElevationDialog, setShowElevationDialog] = useState(false);
  const growerId = useSelector(growerSelectors.selectedId);
  const fields = useSelector(selectors.certifiedFields);
  const selectedAaids = useSelector(selectors.selectedFields);
  const findByAaid = (aaid) => fields?.find((f) => f.aaManagedId === aaid);
  const selectedFields = selectedAaids?.map(findByAaid);
  const isAnySelected = selectedAaids.length > 0;
  const canRunElevation = usePermissions().includes('RUN_ELEVATION');
  const allowedToReassign = usePermissions().includes('ADMIN_GROWERS');
  const isVpiEnabled = useFeatureFlag('ProductPlacementVPI');
  const { showInfo } = useAlerts();
  const showActions = usePermission('VIEW_ACTIONS');

  const runElevation = useCallback(runElevationCallback, [
    selectedAaids,
    showInfo,
  ]);

  const renderActions = useCallback(
    () => (
      <ActionButton>
        <Button
          onClick={() => setEditAttributes(selectedAaids)}
          disabled={!isAnySelected}
        >
          Edit Attributes
        </Button>
        <Button
          onClick={() => setUncertifyFields(selectedFields)}
          disabled={!isAnySelected}
        >
          Uncertify
        </Button>
        <Button
          onClick={() => exportCsv(isAnySelected ? selectedFields : fields)}
          disabled={!fields?.length}
        >
          Export CSV
        </Button>
        {canRunElevation ? (
          <Button
            onClick={() => {
              hexbinService.generate(selectedAaids);
              showInfo('Hexbin generation scheduled');
            }}
            disabled={!isAnySelected}
          >
            Generate Hexbins
          </Button>
        ) : null}
        {canRunElevation ? (
          <Button
            onClick={() => setShowElevationDialog(true)}
            disabled={!isAnySelected}
          >
            Generate Elevation
          </Button>
        ) : null}
        {canRunElevation ? (
          <Button
            onClick={() => {
              soilService.interpolate(selectedAaids);
              showInfo('Soil interpolation scheduled');
            }}
            disabled={!isAnySelected}
          >
            Interpolate Soil
          </Button>
        ) : null}
        {allowedToReassign ? (
          <Button
            onClick={() => setReassignFields(selectedAaids)}
            disabled={!isAnySelected}
          >
            Reassign Fields
          </Button>
        ) : null}
        {isVpiEnabled ? (
          <DownloadButton
            disabled={!isAnySelected}
            keep-open="true"
            variant="text"
            size="medium"
            download={() =>
              vpiService.downloadVPISummary(growerId, selectedAaids)
            }
          >
            Print VPI Summary
          </DownloadButton>
        ) : null}
      </ActionButton>
    ),
    [
      canRunElevation,
      fields,
      isAnySelected,
      selectedAaids,
      selectedFields,
      allowedToReassign,
      showInfo,
      isVpiEnabled,
      growerId,
    ],
  );

  return (
    <>
      {editAttributes && buildEditAttributes()}
      {uncertifyFields && buildUncertifyFields()}
      {reassignFields && buildReassignFields()}
      {showElevationDialog && (
        <ElevationDialog
          onCancel={() => setShowElevationDialog(false)}
          onClose={handleElevationDialogClose}
        />
      )}

      <Tab
        type="certified"
        columns={buildColumns()}
        isActive={isActive}
        renderTabs={renderTabs}
        renderActions={showActions ? renderActions : undefined}
      />
    </>
  );

  function buildEditAttributes() {
    return (
      <EditAttributes
        fieldsInfo={{ type: 'CERTIFIED', aaManagedIds: editAttributes }}
        close={() => setEditAttributes(null)}
        save={saveAttributes}
      />
    );
  }

  function saveAttributes(fieldsInfo, attributes) {
    dispatch(actions.saveAttributes(growerId, fieldsInfo, attributes));
  }

  function buildUncertifyFields() {
    return (
      <UncertifyFields
        fields={uncertifyFields}
        close={() => setUncertifyFields(null)}
      />
    );
  }

  function buildReassignFields() {
    return (
      <ReassignFields
        fieldsToReassign={reassignFields}
        close={() => setReassignFields(null)}
        save={saveReassignedFields}
      />
    );
  }

  function saveReassignedFields(newGrowerId, fieldsToReassign) {
    dispatch(actions.reassignFields(growerId, newGrowerId, fieldsToReassign));
    showInfo('Field reassignments have been scheduled.');
  }

  function handleElevationDialogClose(options) {
    setShowElevationDialog(false);
    runElevation(options);
  }

  function runElevationCallback(options) {
    elevationService.generate(selectedAaids, options);
    showInfo('Elevation generation scheduled');
  }
}

function buildColumns() {
  return [
    {
      field: 'plannedCrop',
      headerName: 'Planned Crop',
      flex: 0.5,
    },
    {
      field: 'rowSpacing',
      type: 'number',
      headerName: 'Row Spacing',
      flex: 0.5,
    },
    {
      field: 'earlyFungicide',
      headerName: 'Early Fungicide',
      flex: 0.5,
    },
    {
      field: 'lateFungicide',
      headerName: 'Late Fungicide',
      flex: 0.5,
    },
    {
      field: 'tillageType',
      headerName: 'Tillage Type',
      flex: 0.5,
    },
    {
      field: 'coverCrop',
      headerName: 'Cover Crop',
      flex: 0.5,
    },
    {
      field: 'starterFertilizer',
      headerName: 'Starter Fertilizer',
      flex: 0.5,
    },
    {
      field: 'traitPackage',
      headerName: 'Trait Package',
      flex: 0.5,
    },
    {
      field: 'createdDate',
      type: 'date',
      headerName: 'Date Updated',
      flex: 0.75,
      renderCell: (params) => (
        <TableCell renderCellParams={params}>
          <LocalDate value={params.row.createdDate} />
        </TableCell>
      ),
    },
  ];
}
